import type { DerivedStatId, StatChange } from "odyssey-shared";
import { say } from "../../words.ts";

/**
 * How the preview panel prints a derived stat (ADR-138's amendment of 2026-10-31, decision 5): the pools as whole
 * points, the chances and the changes as percents from their basis points, a change signed.
 */

const POOLS: ReadonlySet<DerivedStatId> = new Set(["maxHealth", "maxFocus"]);
const CHANCES: ReadonlySet<DerivedStatId> = new Set(["critical", "dodge"]);

/**
 * Basis points as a percent with at most one decimal.
 *
 * @param basisPoints The value.
 * @returns The digits, without a sign.
 */
function percentDigits(basisPoints: number): string {
  const tenths = Math.round(Math.abs(basisPoints) / 10);
  return tenths % 10 === 0 ? String(tenths / 10) : (tenths / 10).toFixed(1);
}

/**
 * A stat's value as the panel prints it.
 *
 * @param id The stat.
 * @param value Its value.
 * @returns The text: `120`, `5.3%` or `+4%`.
 */
export function statValue(id: DerivedStatId, value: number): string {
  if (POOLS.has(id)) {
    return String(value);
  }
  if (CHANCES.has(id)) {
    return say("character.preview.percent", { value: percentDigits(value) });
  }
  return say("character.preview.signedPercent", { sign: value < 0 ? "-" : "+", value: percentDigits(value) });
}

/**
 * A change's delta as the panel prints it: `+9` for a pool, `+0.5%` for a chance or a change.
 *
 * @param change The change.
 * @returns The text, signed.
 */
export function statDelta(change: StatChange): string {
  const delta = change.after - change.before;
  const sign = delta < 0 ? "-" : "+";
  return POOLS.has(change.id) ? `${sign}${Math.abs(delta)}` : say("character.preview.signedPercent", { sign, value: percentDigits(delta) });
}

/**
 * Whether a change is good for the character: more of everything but the damage it takes, its blow cooldown and the
 * crowd control on it.
 *
 * @param change The change.
 * @returns True when the change helps.
 */
export function statHelps(change: StatChange): boolean {
  const lower = change.id === "physicalTaken" || change.id === "blowCooldown" || change.id === "controlDuration";
  return lower ? change.after < change.before : change.after > change.before;
}

/**
 * A stat's name.
 *
 * @param id The stat.
 * @returns Its name from the language files.
 */
export function statName(id: DerivedStatId): string {
  return say(`character.preview.stats.${id}`);
}
